import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bot, ChevronDown, Check } from 'lucide-react';
import { listModelCatalog } from 'features/evals3/api/evals3Api';

/**
 * The provider and model pickers a judge node uses — EXACTLY the controls
 * the current evals' editor has, duplicated (the two systems share no code).
 *
 * Provider = one of the team's LLM credentials; model = a catalog model for
 * that provider, or the credential's own deployment list for the providers
 * whose model ids are user-defined (Azure, OpenAI-compatible).
 */

export const PROVIDER_ICON = {
  openai: '/icons/model_icons/openai.svg',
  anthropic: '/icons/model_icons/anthropic.png',
  gemini: '/icons/model_icons/gemini.png',
  mistral: '/icons/model_icons/mistralai.png',
  fireworks: '/icons/model_icons/fireworksai.png',
  groq: '/icons/model_icons/groq.png',
  deepseek: '/icons/model_icons/deepseek.png',
  xai: '/icons/model_icons/grok.png',
  azure_openai: '/icons/model_icons/azureai.png',
  vertex_ai: '/icons/model_icons/vertexai.png',
  jev: '/icons/model_icons/typesafe.png',
  bedrock: '/icons/model_icons/bedrock.png',
  trodo: '/trodologo.svg',
};
export const PROVIDER_LABEL = {
  openai: 'OpenAI', anthropic: 'Anthropic', gemini: 'Gemini', mistral: 'Mistral AI', fireworks: 'Fireworks AI', groq: 'Groq',
  deepseek: 'DeepSeek', xai: 'xAI', openai_compat: 'OpenAI-compatible endpoint', azure_openai: 'Azure OpenAI', vertex_ai: 'Vertex AI', bedrock: 'Amazon Bedrock',
  jev: 'Jev', trodo: 'Hartley',
};
const LOGO_SCALE = { azure_openai: 1.35 };
const NEEDS_WHITE_BG = new Set(['openai', 'xai', 'deepseek']);
export const MANUAL_MODEL_PROVIDERS = new Set(['azure_openai', 'openai_compat']);

export function ProviderLogo({ provider, size = 16 }) {
  const src = PROVIDER_ICON[provider];
  if (!src) {
    return (
      <span style={{ width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-hover)', border: '1px solid var(--border-default)', borderRadius: '50%', flexShrink: 0 }}>
        <Bot style={{ width: size - 6, height: size - 6, color: 'var(--text-muted)' }} />
      </span>
    );
  }
  const scale = LOGO_SCALE[provider] || 1;
  const white = NEEDS_WHITE_BG.has(provider);
  return (
    <span style={{ width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderRadius: '50%', background: white ? '#ffffff' : 'transparent', padding: white ? 1 : 0, boxSizing: 'content-box' }}>
      <img src={src} alt="" style={{ width: size * scale, height: size * scale, objectFit: 'contain' }} />
    </span>
  );
}

function useClickOutside(ref, open, close) {
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [ref, open, close]);
}

export function ProviderDropdown({ integrations, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(wrapRef, open, close);
  const selected = (integrations || []).find((i) => String(i.id) === String(value));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" className="input" disabled={disabled} onClick={() => setOpen((o) => !o)} aria-label="Provider"
        style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer' }}>
        {selected && <ProviderLogo provider={selected.provider} size={16} />}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? `${PROVIDER_LABEL[selected.provider] || selected.provider} · ${selected.label}` : 'Connect a provider…'}
        </span>
        <ChevronDown style={{ width: 12, height: 12, color: 'var(--text-muted)', flexShrink: 0 }} />
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-[color:var(--surface-base)] border border-[color:var(--border-default)] rounded-lg shadow-lg" style={{ maxHeight: 280, overflowY: 'auto' }} role="listbox">
          {(integrations || []).length === 0 && (
            <div className="px-3 py-3 text-[11.5px] text-[color:var(--text-muted)] text-center">No providers yet. Add one in Settings → Models.</div>
          )}
          {(integrations || []).map((i) => {
            const isSel = String(i.id) === String(value);
            return (
              <button key={i.id} type="button" role="option" aria-selected={isSel} onClick={() => { onChange(String(i.id)); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] ${isSel ? 'bg-[color:var(--surface-hover)]' : 'hover:bg-[color:var(--surface-hover)]'}`}>
                <ProviderLogo provider={i.provider} size={16} />
                <span className="flex-1 min-w-0">
                  <div className="text-[color:var(--text-primary)] truncate">{i.label}</div>
                  <div className="text-[10.5px] text-[color:var(--text-muted)] truncate">{PROVIDER_LABEL[i.provider] || i.provider} · {i.key_prefix || ''}</div>
                </span>
                {isSel && <Check style={{ width: 12, height: 12, color: 'var(--text-primary)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ModelDropdown({ integration, models: modelsProp, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(wrapRef, open, close);
  const models = (modelsProp && modelsProp.length) ? modelsProp : (integration?.config?.models || []);

  if (!integration) {
    return <div className="input" style={{ color: 'var(--text-muted)', cursor: 'not-allowed', background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center' }}>Pick a provider first</div>;
  }
  if (models.length === 0) {
    const manual = MANUAL_MODEL_PROVIDERS.has(integration.provider);
    return <div className="input" style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}>{manual ? 'No deployments. Add them in Settings → Models.' : 'Loading models…'}</div>;
  }
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" className="input" disabled={disabled} onClick={() => setOpen((o) => !o)} aria-label="Model"
        style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: value ? 'monospace' : 'inherit', color: value ? 'var(--text-primary)' : 'var(--text-muted)' }}>
          {value || 'Select a model'}
        </span>
        <ChevronDown style={{ width: 12, height: 12, color: 'var(--text-muted)', flexShrink: 0 }} />
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-[color:var(--surface-base)] border border-[color:var(--border-default)] rounded-lg shadow-lg" style={{ maxHeight: 280, overflowY: 'auto' }} role="listbox">
          {models.map((m) => {
            const isSel = m === value;
            return (
              <button key={m} type="button" role="option" aria-selected={isSel} onClick={() => { onChange(m); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] font-mono ${isSel ? 'bg-[color:var(--surface-hover)]' : 'hover:bg-[color:var(--surface-hover)]'}`}>
                <span className="flex-1 min-w-0 truncate text-[color:var(--text-primary)]">{m}</span>
                {isSel && <Check style={{ width: 12, height: 12, color: 'var(--text-primary)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * credential → model ids. Catalog-backed providers are fetched once per
 * provider (module cache); manual providers use their own deployment list.
 */
const catalogCache = new Map();
export function useCatalogModels(teamId, integrations) {
  const [byProvider, setByProvider] = useState({});
  const providerKey = [...new Set((integrations || []).map((i) => i && i.provider).filter(Boolean))]
    .filter((p) => !MANUAL_MODEL_PROVIDERS.has(p)).sort().join(',');

  useEffect(() => {
    if (!providerKey) return undefined;
    let cancelled = false;
    Promise.all(providerKey.split(',').map(async (p) => {
      if (catalogCache.has(p)) return [p, catalogCache.get(p)];
      try {
        const models = await listModelCatalog(teamId, p);
        catalogCache.set(p, models);
        return [p, models];
      } catch (_) { return [p, []]; }
    })).then((pairs) => { if (!cancelled) setByProvider(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [teamId, providerKey]);

  return useCallback((cred) => {
    if (!cred) return [];
    if (MANUAL_MODEL_PROVIDERS.has(cred.provider)) return cred.config?.models || [];
    const cat = byProvider[cred.provider];
    if (cat && cat.length) return cat.map((m) => m.id);
    return cred.config?.models || [];
  }, [byProvider]);
}
